import type { OptionalStatuses } from '@/types/claim'
import type { ContractRequest } from '@/types/contractRequests'
import {
  ArchivedContractStatuses,
  ContractStatus,
  InProgressContractStatuses,
  PendingContractStatuses
} from '@/types/contractStatuses'
import type { Contract } from '@/types/contracts'
import { type Document, type InsuranceCertificate } from '@/types/documents'
import { DocumentStatuses } from '@/types/documents'
import type { Me } from '@/types/me'
import { LifeProductTypes } from '@/types/productTypes'

import type { BrokerKey } from './brokers'

//* Business logic to allow the user to declare a claim on a contract
// `allowTerminatedContract` should only be set to true from the contract detail page: a claim can still be declared
// on a resiliated (archived) contract there, but resiliated contracts must stay excluded from the contract picker
// used when declaring a claim without a preselected contract.
export const allowReportingClaim = (contract: Contract | ContractRequest, allowTerminatedContract = false) => {
  switch (true) {
    case LifeProductTypes.includes(contract.productType):
      // not allowed on life contract
      return false
    case InProgressContractStatuses.includes(contract.status):
      return true
    case allowTerminatedContract && ArchivedContractStatuses.includes(contract.status):
      return true
    default:
      // not allowed on contract that are not in progress
      return false
  }
}

//* Notifications related - START
export const contractWithPendingSignature = (contract: Contract) => {
  return (
    !ArchivedContractStatuses.includes(contract.status) &&
    (contract.pendingSignature || contract.amendmentPendingSignature)
  )
}

export const contractWithPendingDocument = (contract: Contract) => {
  return PendingContractStatuses.includes(contract.status) && contract.missingDocumentsCount > 0
}

export const contractWithPendingPayment = (contract: Contract) => {
  return !ArchivedContractStatuses.includes(contract.status) && !!contract.unpaidPending
}

export const contractRequestWithPendingDocument = (contractRequest: ContractRequest) => {
  return PendingContractStatuses.includes(contractRequest.status) && contractRequest.missingDocumentsCount > 0
}

export const contractRequestWithPendingPayment = (contractRequest: ContractRequest) => {
  return contractRequest.status === ContractStatus.WaitingForPayment
}
//* Notifications related - END

export const hasSelectedCivilLiabilityCover = (contract: Contract | ContractRequest): boolean =>
  contract.covers.length === 0 || contract.covers.some((belonging) => belonging.selected.includes('rc'))

export const canRequestGreenCard = (contract: Contract, documents: Document[]) => {
  return (
    hasSelectedCivilLiabilityCover(contract) &&
    !documents.filter((document) => document.type === 'green_card' && document.status === DocumentStatuses.Processed)
      ?.length &&
    !contract.baloisePack &&
    InProgressContractStatuses.includes(contract.status)
  )
}

export const claimStatuses = (optionalStatuses: OptionalStatuses) => {
  const statuses = [
    'reported',
    'analysis_and_awaiting_possible_expertise',
    'awaiting_report',
    'awaiting_liability',
    'awaiting_closure',
    'closed'
  ]

  if (optionalStatuses.expertiseDispute) statuses.push('expertise_dispute')
  if (optionalStatuses.liabilityDispute) statuses.push('liability_dispute')
  if (optionalStatuses.awaitingCriminalFile) statuses.push('awaiting_criminal_file')
  if (optionalStatuses.awaitingLawyerFeedback) statuses.push('awaiting_lawyer_feedback')

  // timeline order
  const orderedStatuses = [
    'reported',
    'analysis_and_awaiting_possible_expertise',
    'expertise_dispute',
    'awaiting_report',
    'awaiting_liability',
    'liability_dispute',
    'awaiting_criminal_file',
    'awaiting_lawyer_feedback',
    'awaiting_closure',
    'closed'
  ]

  return statuses.sort((a, b) => {
    return orderedStatuses.indexOf(a) - orderedStatuses.indexOf(b)
  })
}

export const certificatesWithValidity = (certificates: InsuranceCertificate[]) => {
  return certificates
    .filter((certificate) => certificate.date.fr !== 'unknown')
    .reduce(
      (acc, certificate) => {
        if (certificate.date.fr === 'current') acc.current.push(certificate)
        else acc.archived.push(certificate)
        return acc
      },
      { current: [] as InsuranceCertificate[], archived: [] as InsuranceCertificate[] }
    )
}

export const certificatesWithoutValidity = (certificates: InsuranceCertificate[]) => {
  const allCertificates = certificates
    .filter((certificate) => certificate.date.fr === 'unknown')
    .sort((c1, c2) => {
      if (c1.receiveTime && c2.receiveTime) return Date.parse(c2.receiveTime) - Date.parse(c1.receiveTime)
      else return 0
    })

  const latestReceiveTime = allCertificates[0]?.receiveTime

  if (!latestReceiveTime || Date.parse(latestReceiveTime) < oneYearAgo()) {
    return { current: [], archived: allCertificates }
  } else {
    return allCertificates.reduce(
      (acc, certificate) => {
        if (certificate.receiveTime === latestReceiveTime) acc.current.push(certificate)
        else acc.archived.push(certificate)
        return acc
      },
      { current: [] as InsuranceCertificate[], archived: [] as InsuranceCertificate[] }
    )
  }
}

const oneYearAgo = () => {
  const oneYearAgo = new Date()
  return oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
}

export const certificatesByStatus = (certificates: InsuranceCertificate[]) => {
  const withValidity = certificatesWithValidity(certificates)
  const withoutValidity = certificatesWithoutValidity(certificates)
  return {
    current: withValidity.current.concat(withoutValidity.current),
    archived: withValidity.archived.concat(withoutValidity.archived)
  }
}

export const contractFormParams = (contract: Contract, me: Me, broker: BrokerKey, companyId: string | null) => {
  return `broker=${broker}&contract_type=${contract.productType}&contract_number=${contract.reference}&email=${
    me.email
  }&first_name=${me.firstName}&last_name=${me.lastName}&company_id=${companyId ? companyId : ''}`
}
